import React, { useState, useEffect } from 'react';
import axios from 'axios';
import GlassCard from './GlassCard';
import GlassButton from './GlassButton';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Target, Brain } from 'lucide-react';

interface Recommendation {
  symbol: string;
  asset_type: string;
  current_weight: number;
  suggested_weight: number;
  delta: number;
  confidence: number;
  action: 'OVERWEIGHT' | 'UNDERWEIGHT';
}

interface Strategy {
  strategy_id: string;
  recommendations: Recommendation[];
  system_audit: string;
  timestamp: string;
}

const OptimizationGrid: React.FC = () => {
  const [strategy, setStrategy] = useState<Strategy | null>(null);
  const [loading, setLoading] = useState(true);

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';

  useEffect(() => {
    const fetchOptimization = async () => {
      try {
        // In actual bridge, we'd pass user_id. 
        // For Wave 2 demo, we use a mock-integrated retrieval.
        const response = await axios.get(`${apiUrl}/api/v1/ai/optimization-recommendations?user_id=89234`);
        setStrategy(response.data);
      } catch (err) {
        console.error('Neural Optimization link failed', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOptimization();
  }, []);

  if (loading) return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-pulse">
       {[1,2,3,4].map(i => <div key={i} className="h-40 bg-white/5 rounded-3xl" />)}
    </div>
  );

  if (!strategy || strategy.recommendations.length === 0) return null;

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center px-1">
        <div className="flex items-center gap-4">
           <div className="p-3 bg-brand-teal/10 rounded-2xl border border-brand-teal/20 text-brand-teal-light">
             <Brain size={24} />
           </div>
           <div>
              <h2 className="text-xl font-black text-text-primary tracking-tighter uppercase">Neural <span className="text-brand-teal-light">Optimization Grid</span></h2>
              <p className="text-[10px] text-text-tertiary font-bold uppercase tracking-widest opacity-50">Regime-Aware Rebalancing Logic</p>
           </div>
        </div>
        <div className="flex items-center gap-3">
           <div className="text-right mr-4">
              <div className="text-[9px] font-black uppercase tracking-widest text-text-tertiary mb-1">Strategy ID</div>
              <div className="text-xs font-mono font-bold text-text-secondary">#RT-{strategy.strategy_id.split('-')[1]}</div>
           </div>
           <GlassButton variant="primary" size="sm" className="h-12 px-6 rounded-2xl uppercase tracking-widest font-black text-[10px]" glowEffect>
              Initiate Neural Rebalance
           </GlassButton>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {strategy.recommendations.map((rec, i) => (
          <motion.div
            key={rec.symbol}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: i * 0.1 }}
          >
            <GlassCard variant="glow" className="p-8 group relative overflow-hidden">
               <div className={`absolute top-0 right-0 w-16 h-16 blur-2xl opacity-10 ${rec.action === 'OVERWEIGHT' ? 'bg-brand-teal' : 'bg-brand-plum'}`} />
               
               <div className="flex justify-between items-start mb-6">
                 <div>
                   <div className="text-[10px] font-black uppercase tracking-widest text-text-tertiary mb-1">{rec.asset_type} NODE</div>
                   <div className="text-2xl font-black text-white tracking-tighter">{rec.symbol}</div>
                 </div>
                 <div className={`p-2 rounded-xl flex items-center justify-center ${rec.action === 'OVERWEIGHT' ? 'bg-brand-teal/10 text-brand-teal' : 'bg-brand-plum/10 text-brand-plum'}`}>
                    {rec.action === 'OVERWEIGHT' ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
                 </div>
               </div>

               <div className="space-y-4">
                  <div className="flex justify-between items-center text-[11px] font-black uppercase tracking-widest">
                     <span className="text-text-tertiary">Suggestion</span>
                     <span className={rec.action === 'OVERWEIGHT' ? 'text-brand-teal-light' : 'text-brand-plum-light'}>
                        {rec.action} ({Math.abs(rec.delta * 100).toFixed(1)}%)
                     </span>
                  </div>
                  
                  <div className="relative h-1 w-full bg-white/5 rounded-full overflow-hidden">
                     <div 
                        className="absolute left-0 top-0 h-full bg-white/10" 
                        style={{ width: `${rec.current_weight * 100}%` }} 
                     />
                     <motion.div 
                        initial={{ width: 0 }}
                        animate={{ width: `${rec.suggested_weight * 100}%` }}
                        className={`absolute left-0 top-0 h-full ${rec.action === 'OVERWEIGHT' ? 'bg-brand-teal' : 'bg-brand-plum'}`} 
                     />
                  </div>

                  <div className="flex justify-between items-center bg-white/[0.02] p-4 rounded-2xl border border-white/5">
                     <div className="space-y-1">
                        <div className="text-[8px] font-black uppercase tracking-widest text-text-tertiary">Neural Bias</div>
                        <div className="text-[10px] font-black text-text-primary">{(rec.confidence * 100).toFixed(0)}% PROBABILITY</div>
                     </div>
                     <Target size={16} className="text-brand-teal opacity-30" />
                  </div>
               </div>
            </GlassCard>
          </motion.div>
        ))}
      </div>

      <GlassCard variant="subtle" className="p-6 border-white/5 flex items-center gap-4 text-text-tertiary">
         <div className="w-1 h-8 bg-brand-teal rounded-full" />
         <p className="text-[11px] font-bold uppercase tracking-widest leading-loose">
            <span className="text-brand-teal-light font-black mr-2">Audit Verdict:</span>
            {strategy.system_audit}
         </p>
      </GlassCard>
    </div>
  );
};

export default OptimizationGrid;
